import { Button, HouseIcon } from '@/components/atoms';
import { CalendarFoldIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { getReservationToneColor, type ReservationStatus } from '@/core/reservation';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import { ReservationStatusMessage } from './ReservationStatusMessage';

export type ReservationSignatureStatus =
  | 'awaiting_signature'
  | 'check_email'
  | 'confirmed';

export interface ReservationStatusCardProps {
  /** Reservation date display, e.g. "5 August 2026" */
  reservedDateDisplay: string;
  status: ReservationSignatureStatus;
  mappedStatus: ReservationStatus | null;
  /** Raw SF status for ADHA Contentful `reservationStatus.<normalized>` lookup. */
  rawStatus?: string | null;
  onCancel: () => void;
  canCancelReservation: boolean;
}

const META_ICON_SIZE = 16;

/**
 * Desktop sidebar card after unit is reserved.
 * Title stays "Unit Reserved" (no Pending / In Progress / Confirmed heading).
 * Cancel stays available when the reservation can still be cancelled.
 */
export function ReservationStatusCard({
  reservedDateDisplay,
  mappedStatus,
  rawStatus,
  onCancel,
  canCancelReservation,
}: Readonly<ReservationStatusCardProps>) {
  const colors = useUIColors();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const statusColor = mappedStatus
    ? getReservationToneColor(mappedStatus, colors)
    : colors.reservationPendingText;

  return (
    <View
      style={[styles.card, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <View style={styles.headerRow}>
        <View
          style={[
            styles.iconTile,
            { backgroundColor: UI_PRIMITIVES.greenLighter },
          ]}
        >
          <HouseIcon size={24} color={colors.reserveIconTint} />
        </View>
        <View style={styles.headerText}>
          <Typography
            variant="title3"
            color="primary"
            style={styles.title}
          >
            {labels.UNIT_RESERVED_TITLE}
          </Typography>
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <CalendarFoldIcon
                size={META_ICON_SIZE}
                color={colors.fontSecondary}
              />
              <Typography
                variant="body"
                color="secondary"
                style={styles.metaText}
              >
                {reservedDateDisplay}
              </Typography>
            </View>
            <View style={styles.metaItem}>
              <ReservationStatusMessage
                mappedStatus={mappedStatus}
                rawStatus={rawStatus ?? null}
                color={statusColor}
                iconColor={statusColor}
                variant="body"
                style={styles.metaText}
                skeletonWidth={120}
                skeletonHeight={16}
                showToneIcon
                iconSize={META_ICON_SIZE}
              />
            </View>
          </View>
        </View>
      </View>

      {canCancelReservation ? (
        <Button
          title={labels.CANCEL_RESERVATION_TITLE}
          variant="outline"
          fullWidth
          onPress={onCancel}
          style={styles.cancelButton}
          textStyle={styles.actionLabel}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    /** Figma frame — header | email | buttons */
    gap: 24,
    alignSelf: 'stretch',
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  iconTile: {
    padding: 16,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  /** Reservation In Progress — Size/L Medium, LH 100%, #333 */
  title: {
    lineHeight: 20,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  /** Date + status — Base Regular, LH 22 (confirmed #388E3C via iskanLoanText) */
  metaText: {
    lineHeight: 22,
    fontWeight: '400',
  },
  /** Dashed divider + email row — tight so card gap:24 is the only header↔email space */
  emailSection: {
    gap: 12,
  },
  dashedLine: {
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    width: '100%',
  },
  emailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  emailText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  /** Check Your Email — Base SemiBold, LH 100%, #4D4D4D */
  emailTitle: {
    lineHeight: 16,
  },
  /** Body — 3XS Regular, LH 100%, #4D4D4D */
  emailBody: {
    lineHeight: 12,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionHalf: {
    flex: 1,
    minWidth: 0,
  },
  /** Transparent fill — sits on grey card, not white */
  cancelButton: {
    backgroundColor: 'transparent',
    borderRadius: 4,
  },
  actionLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default ReservationStatusCard;
